import * as SwitchRadix from '@radix-ui/react-switch';
import { styled } from 'styled-components';

const ContainerSC = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  width: 100%;
  user-select: none;
  cursor: pointer;
`;

const SwitchThumb = styled(SwitchRadix.Thumb)<{ $isDarkMode: boolean }>`
  display: block;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  box-shadow: 0 0.125rem 0.125rem rgba(2, 27, 65, 0.16);
  background-color: ${(props) => props.theme.colors.white};
  transition: transform 100ms;
  will-change: transform;
  transform: ${(props) => (props.theme.isRtl ? 'translateX(-0.125rem);' : 'translateX(0.125rem);')};
  &[data-state='checked'] {
    transform: ${(props) => (props.theme.isRtl ? 'translateX(-1.625rem)' : 'translateX(1.625rem)')};
  }
  &[data-disabled] {
    background-color: ${(props) => (props.$isDarkMode ? props.theme.colors.oxfordBlue[80] : 'rgba(255,255,255,.5)')};
  }
`;

const SwitchRoot = styled(SwitchRadix.Root)<{ $isDarkMode: boolean }>`
  all: unset;
  width: 3.5rem;
  height: 2rem;
  border-radius: 1rem;
  border-width: 0rem;
  position: relative;
  flex-shrink: 0;
  cursor: ${(props) => (props.disabled ? 'auto' : 'pointer')};
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  &[data-state='checked'] {
    background-color: ${(props) => props.theme.colors.emeraldGreen[100]};
  }

  &[data-state='unchecked'] {
    background-color: ${(props) =>
      props.$isDarkMode ? props.theme.colors.oxfordBlue[100] : props.theme.colors.slateGray[80]};
  }
`;

const LabelSC = styled.label<{ $isDarkMode: boolean }>`
  font-size: 1rem;
  font-weight: 600;
  color: ${(props) => (props.$isDarkMode ? props.theme.colors.white : props.theme.colors.oxfordBlue[100])};
`;

export type SwitchProps = {
  className?: string;
  'data-test-id'?: string;
  isChecked: boolean;
  isDarkMode?: boolean;
  isDisabled?: boolean;
  label: string;
  onChange: () => void;
};

export const Switch = ({
  className,
  'data-test-id': dataTestId,
  isChecked,
  isDarkMode = false,
  isDisabled = false,
  label,
  onChange,
}: SwitchProps) => {
  return (
    <ContainerSC data-test-id={dataTestId}>
      <LabelSC $isDarkMode={isDarkMode}>{label}</LabelSC>
      <SwitchRoot
        $isDarkMode={isDarkMode}
        checked={isChecked}
        className={className}
        disabled={isDisabled}
        onCheckedChange={onChange}
      >
        <SwitchThumb $isDarkMode={isDarkMode} />
      </SwitchRoot>
    </ContainerSC>
  );
};
